import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {selectCountriesCatalog} from "../../../selectors/CatalogSelectors";
import {selectCountries} from "../../VED/redux/selectors/FactorySelectors";
import {Select} from "../SelectField";

import {REGION_CODES} from "./constants";

const normCountryName = value =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zа-яіїєґ0-9]+/gi, " ")
    .trim();

const DISPLAY_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], {type: "region"})
    : null;

const ISO_BY_NAME = REGION_CODES.reduce((acc, code) => {
  if (!DISPLAY_NAMES) return acc;
  const label = DISPLAY_NAMES.of(code);
  if (label) {
    acc.set(normCountryName(label), code);
  }
  return acc;
}, new Map());

const COUNTRY_NAME_ALIASES = new Map([
  ["usa", "US"],
  ["us", "US"],
  ["united states", "US"],
  ["united states of america", "US"],
  ["uk", "GB"],
  ["great britain", "GB"],
  ["england", "GB"],
  ["scotland", "GB"],
  ["wales", "GB"],
  ["czech republic", "CZ"],
  ["czechia", "CZ"],
  ["south korea", "KR"],
  ["north korea", "KP"],
  ["russia", "RU"],
  ["moldova", "MD"],
  ["vatican", "VA"],
  ["uae", "AE"],
]);

const getFlagIconUrl = code => {
  if (!code || !/^[A-Z]{2}$/.test(code)) return null;
  return `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
};

const resolveCountryCode = country => {
  const explicitCode = [
    country?.countryCode,
    country?.countryCode2,
    country?.shortCode,
    country?.iso2,
    country?.isoCode,
    country?.code,
  ]
    .find(item => typeof item === "string" && item.trim().length === 2)
    ?.trim()
    .toUpperCase();

  if (explicitCode) return explicitCode;

  const names = [country?.shortNameEng, country?.shortName, country?.title, country?.name];

  for (const rawName of names) {
    const normalizedName = normCountryName(rawName);
    if (!normalizedName) continue;

    const aliasCode = COUNTRY_NAME_ALIASES.get(normalizedName);
    if (aliasCode) return aliasCode;

    const guessedCode = ISO_BY_NAME.get(normalizedName);
    if (guessedCode) return guessedCode;
  }

  return null;
};

const withFlag = (title, countryData) => {
  const code = resolveCountryCode({...countryData, title});
  return {
    title,
    iconSrc: getFlagIconUrl(code),
  };
};

const CountrySelect = ({
  baseOptions,
  label = "Delivery country",
  name = "countryId",
  required,
  disabled,
  autolabel,
  width,
  generalClassName,
  makeEffect,
  optionsLeft,
  optionsAbove,
  style,
  labelwidth,
  inputClassName,
  labelClassName,
  textMode,
  selectOnly,
  minFilterLength,
  errorField,
  placeholder,
  bottomTooltip,
  tooltipMessage,
  grow,
  noFunctional,
  ...props
}) => {
  const {i18n} = useTranslation();
  const factoryCountries = useSelector(selectCountries);
  const catalogCountries = useSelector(selectCountriesCatalog);
  const countries = factoryCountries?.length > 0 ? factoryCountries : catalogCountries;

  const options = useMemo(() => {
    const lang = (i18n?.language || "uk").toLowerCase();
    const hotOrder = [
      "european union",
      "ukraine",
      "usa",
      "italy",
      "spain",
      "germany",
      "france",
      "poland",
      "czech republic",
      "denmark",
      "australia",
    ];
    const hotAliases = new Map([
      ["usa", ["usa", "united states", "united states of america", "сша", "сполучені штати америки"]],
      ["european union", ["країни єс", "eu countries", "eu"]],
      [("czech republic", ["czech republic", "czechia", "чехія", "чеська республіка"])],
      ["ukraine", ["ukraine", "україна"]],
      ["italy", ["italy", "італія"]],
      ["spain", ["spain", "іспанія"]],
      ["germany", ["germany", "німеччина"]],
      ["france", ["france", "франція"]],
      ["poland", ["poland", "польща"]],
      ["denmark", ["denmark", "данія"]],
      ["australia", ["australia", "австралія"]],
    ]);

    const fromRedux = Array.isArray(countries) && countries.length > 0;
    const base = Array.isArray(baseOptions) && baseOptions.length > 0 ? baseOptions : null;
    const countriesById = new Map((countries || []).map(c => [String(c?.countryId), c]));

    const list = base
      ? base.map((opt, idx) => {
          const linkedCountry = countriesById.get(String(opt?.value));
          const countryData = linkedCountry || opt;
          const optionWithFlag = withFlag(opt.title, countryData);

          return {
            id: opt.value,
            rawTitle: opt.title,
            title: optionWithFlag.title,
            iconSrc: optionWithFlag.iconSrc,
            idx,
          };
        })
      : fromRedux
      ? countries.map((c, idx) => {
          const rawTitle = lang.startsWith("en") ? c.shortNameEng || c.shortName : c.shortName || c.shortNameEng;
          const optionWithFlag = withFlag(rawTitle, c);

          return {
            id: c.countryId,
            rawTitle,
            title: optionWithFlag.title,
            iconSrc: optionWithFlag.iconSrc,
            idx,
          };
        })
      : [];

    const norm = s => (s || "").toLowerCase().trim();
    const rankMap = new Map();
    hotOrder.forEach((key, i) => {
      rankMap.set(key, i);
      (hotAliases.get(key) || []).forEach(alias => rankMap.set(alias, i));
    });

    const mapped = list.map(({id, title, rawTitle, iconSrc, idx}) => {
      const r = rankMap.get(norm(rawTitle || title));
      return {
        value: id,
        title,
        iconSrc,
        _rank: typeof r === "number" ? r : null,
        _orig: idx,
      };
    });

    mapped.sort((a, b) => {
      const aHot = a._rank !== null;
      const bHot = b._rank !== null;
      if (aHot !== bHot) return aHot ? -1 : 1; //* hot first
      if (aHot && bHot) return a._rank - b._rank; //*by order
      return a._orig - b._orig;
    });

    return mapped;
  }, [baseOptions, countries, i18n?.language]);

  return (
    <Select
      label={label}
      name={name}
      options={options}
      required={required}
      disabled={disabled}
      autolabel={autolabel}
      width={width}
      generalClassName={generalClassName}
      makeEffect={makeEffect}
      optionsLeft={optionsLeft}
      optionsAbove={optionsAbove}
      style={style}
      labelwidth={labelwidth}
      inputClassName={inputClassName}
      labelClassName={labelClassName}
      textMode={textMode}
      selectOnly={selectOnly}
      minFilterLength={minFilterLength}
      errorField={errorField}
      placeholder={placeholder}
      bottomTooltip={bottomTooltip}
      tooltipMessage={tooltipMessage}
      grow={grow}
      noFunctional={noFunctional}
      {...props}
    />
  );
};

export default CountrySelect;
